import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Select, Option } from '@fiverr-private/fit';

import * as Translations from '../../providers/i18n';
import * as actions from '../../actions/shareableLink';
import * as selectors from '../../selectors/shareableLink';
import reportToBigQuery from '../../../Utils/bigQueryReporter';
import { showErrorToast, showSuccessToast } from '../ButterToasts';
import { sendMixPanelEvent } from '../../../Utils/mixpanel';
import { COPIED_SHAREABLE_LINK } from '../../../Utils/mixpanel/mixpanelEvents';
import copyToClipboard from './copyToClipboard';

import UserIcon from './icons/UserIcon';
import LockIcon from './icons/LockIcon';

import './style.scss';
import statuses from './statuses';

export class StatelessCreateShareableLink extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            currentStatus: statuses.ACTIVE
        };
    }

    setStatus(status, i18n) {
        if (!this.props.link) {
            this.setState({ currentStatus: status });
        }
        else {
            Promise.resolve()
                .then(() => this.props.setLinkStatus(status))
                .catch(() => {
                    this.showChangeStatusErrorMessage(i18n);
                });
        }
    }

    getLink(generateLink, i18n) {
        this.trackUserClickedCopyLinkEvent();

        Promise.resolve()
            .then(() => generateLink())
            .then((link) => {
                copyToClipboard(`${location.origin}/${link.url}`);
                this.showGetLinkSuccessMessage(i18n);
            }).catch(() => {
                this.showGetLinkErrorMessage(i18n);
            });
    }

    showGetLinkSuccessMessage(i18n) {
        const message = i18n.t('attachments_preview.shareable_link.link_copied.success');
        showSuccessToast(message);
    }

    showGetLinkErrorMessage(i18n) {
        const message = i18n.t('attachments_preview.shareable_link.link_copied.failure');
        showErrorToast(message);
    }

    showChangeStatusErrorMessage(i18n) {
        const message = i18n.t('attachments_preview.shareable_link.status_change.failure');
        showErrorToast(message);
    }

    trackUserClickedCopyLinkEvent() {
        reportToBigQuery({
            type: 'clicked_on_copy_shareable_link',
            group: 'user_action',
            action: {
                type: 'click'
            }
        });
        sendMixPanelEvent({
            eventName: COPIED_SHAREABLE_LINK,
            eventData: {
                'Permission Level': 'Public'
            }
        });
    }

    render() {
        const { i18n, generateLink, link, isLoading } = this.props;

        const defaultStateValue = (link && link.status) ? link.status : statuses.ACTIVE;
        const currentStateValue = (link && link.status) ? link.status : this.state.currentStatus;

        const isPublic = currentStateValue === statuses.ACTIVE;
        const Icon = isPublic ? UserIcon : LockIcon;
        const description = isPublic ?
            i18n.t('attachments_preview.shareable_link.public.description') :
            i18n.t('attachments_preview.shareable_link.restricted.description');

        return (
            <div className="shareable-link-container">
                <h3 className="font-accent">
                    {i18n.t('attachments_preview.shareable_link.title')}
                </h3>
                <div className="content">
                    <Avatar>
                        <Icon/>
                    </Avatar>
                    <div className="link-visibility">
                        <div className="visibility-title co-text-darker">
                            <Select defaultValue={defaultStateValue}
                                value={currentStateValue}
                                className="visibility-select"
                                onChange={(e) => this.setStatus(e.target.value, i18n)}>
                                <Option value={statuses.ACTIVE}>
                                    {i18n.t('attachments_preview.shareable_link.public.title')}
                                </Option>
                                <Option value={statuses.SUSPENDED}>
                                    {i18n.t('attachments_preview.shareable_link.restricted.title')}
                                </Option>
                            </Select>
                        </div>
                        <div className="visibility-description co-text-medium">{description}</div>
                    </div>
                    <CopyLinkButton disabled={!isPublic}
                        onClick={() => this.getLink(generateLink, i18n)}
                        i18n={i18n}
                        isLoading={isLoading}/>
                </div>
            </div>
        );
    }
}

function CopyLinkButton({ disabled, onClick, isLoading, i18n }) {
    const containerClassNames = ['copy-link'];
    const textClassNames = ['copy-link-text'];

    const isClickable = !disabled && !isLoading;
    const safeOnClick = isClickable ? onClick : () => null;

    if (disabled) {
        containerClassNames.push('disabled');
    }
    if (isLoading) {
        textClassNames.push('loading');
    }

    return (
        <span className={containerClassNames.join(' ')}
            onClick={safeOnClick}>
            {isLoading && <div className="loader"/>}
            <span className={textClassNames.join(' ')}>
                {i18n.t('attachments_preview.shareable_link.copy_link')}
            </span>
        </span>
    );
}

CopyLinkButton.propTypes = {
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    i18n: PropTypes.object.isRequired
};

StatelessCreateShareableLink.propTypes = {
    i18n: PropTypes.shape({
        t: PropTypes.func
    }),
    generateLink: PropTypes.func.isRequired,
    link: PropTypes.object,
    setLinkStatus: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired
};

function Avatar({ children }) {
    return (
        <div className="avatar">
            {children}
        </div>
    );
}

Avatar.propTypes = {
    children: PropTypes.func
};

const mapStateToProps = (state) => ({
    isLoading: selectors.getIsLoading(state),
    link: selectors.getLink(state)
});

export default compose(
    Translations.connect,
    connect(
        mapStateToProps,
        actions
    ))(StatelessCreateShareableLink);
